import { PlusIcon, XIcon } from "@phosphor-icons/react";
import { Dialog, DialogContent } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useMemo, useState } from "react";

import {
  ADDITIONAL_SECTION_MARKER,
  getAddedSteps,
  type AdditionalBuilderStep,
  type BuilderStep,
} from "@/client/constants/builder-steps";
import { useResumeStore } from "@/client/stores/resume";

import { SECTION_CATALOG } from "./add-section/catalog";
import { SectionChoice } from "./add-section/section-choice";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (step: BuilderStep) => void;
};

export const AddSectionDialog = ({ open, onOpenChange, onCreated }: Props) => {
  const resume = useResumeStore((state) => state.resume);
  const setValue = useResumeStore((state) => state.setValue);
  const [selected, setSelected] = useState<AdditionalBuilderStep | null>(null);
  const [column, setColumn] = useState<0 | 1>(0);
  const added = useMemo(
    () => new Set(getAddedSteps(resume.data.metadata.layout, resume.data.sections)),
    [resume.data.metadata.layout, resume.data.sections],
  );

  const close = () => {
    setSelected(null);
    setColumn(0);
    onOpenChange(false);
  };

  const addSection = () => {
    if (!selected || added.has(selected)) return;
    const layout = structuredClone(resume.data.metadata.layout);
    const pageIndex = Math.max(0, layout.length - 1);
    layout[pageIndex] ??= [[], []];
    layout[pageIndex][column] ??= [];
    layout.forEach((page) =>
      page.forEach((pageColumn) => {
        const existingIndex = pageColumn.indexOf(selected);
        if (existingIndex >= 0) pageColumn.splice(existingIndex, 1);
      }),
    );
    layout[pageIndex][column].push(selected);

    const label = SECTION_CATALOG.find((item) => item.id === selected)?.name ?? selected;
    // Le marqueur Unicode invisible persiste dans le schéma existant et distingue
    // une rubrique réellement ajoutée d'une rubrique seulement présente dans le layout du modèle.
    setValue(`sections.${selected}.name`, `${label}${ADDITIONAL_SECTION_MARKER}`);
    setValue(`sections.${selected}.visible`, true);
    setValue(`sections.${selected}.columns`, column === 0 ? 1 : 2);
    setValue("metadata.layout", layout);
    close();
    onCreated(selected);
  };

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="flex max-h-[min(90dvh,680px)] w-[calc(100vw-1.5rem)] max-w-2xl flex-col overflow-hidden rounded-2xl border border-indigo-100 bg-white p-0 shadow-2xl [&>button]:hidden">
        <header className="flex shrink-0 items-start justify-between border-b border-slate-100 px-4 py-4 sm:px-6">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-[0.14em] text-emerald-600">
              Personnaliser votre CV
            </p>
            <h2 className="mt-1 text-lg font-bold text-[#071451]">Ajouter une section</h2>
            <p className="mt-1 text-[11px] text-slate-500">
              Chaque rubrique dispose de son propre formulaire professionnel.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            aria-label="Fermer"
            className="grid size-8 place-items-center rounded-lg text-slate-500 hover:bg-slate-100"
          >
            <XIcon size={18} weight="bold" />
          </button>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 [scrollbar-color:rgb(165_180_252)_transparent] [scrollbar-width:thin] sm:px-6 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-indigo-300 [&::-webkit-scrollbar]:w-1.5">
          <div className="grid gap-2.5 sm:grid-cols-2">
            {SECTION_CATALOG.map((item) => (
              <SectionChoice
                key={item.id}
                suggestion={item}
                selected={selected === item.id}
                added={added.has(item.id)}
                onSelect={() => setSelected(item.id)}
              />
            ))}
          </div>

          <fieldset className="mt-5">
            <legend className="text-xs font-bold text-[#071451]">Emplacement dans le CV</legend>
            <div className="mt-2 grid grid-cols-2 gap-2.5">
              {(
                [
                  { value: 0, label: "Colonne principale" },
                  { value: 1, label: "Colonne latérale" },
                ] as const
              ).map((item) => (
                <button
                  key={item.value}
                  type="button"
                  aria-pressed={column === item.value}
                  onClick={() => setColumn(item.value)}
                  className={cn(
                    "h-11 rounded-xl border text-[11px] font-bold transition",
                    column === item.value
                      ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                      : "border-slate-200 text-slate-600 hover:border-indigo-300",
                  )}
                >
                  {item.label}
                </button>
              ))}
            </div>
          </fieldset>
        </div>

        <footer className="flex shrink-0 justify-end gap-2 border-t border-slate-100 px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={close}
            className="h-10 rounded-xl border border-slate-200 px-4 text-xs font-bold text-slate-600 hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="button"
            disabled={!selected}
            onClick={addSection}
            className="inline-flex h-10 items-center gap-2 rounded-xl bg-gradient-to-r from-indigo-700 via-blue-600 to-emerald-500 px-4 text-xs font-bold text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-40"
          >
            <PlusIcon size={16} weight="bold" /> Ajouter la section
          </button>
        </footer>
      </DialogContent>
    </Dialog>
  );
};
